import { AuthContext } from '@contexts/AuthContext';
import { AuthService } from '@services/AuthService';
import { useAuthenticationContextState } from '@state/AuthReducer';
import { Log } from '@swyft/core';
import axios from 'axios';
import { User } from 'oidc-client';
import React, { useCallback, useEffect, useRef } from 'react';

const parseJwt = (token: string) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace('-', '+').replace('_', '/');
  return JSON.parse(window.atob(base64));
};

export interface AuthProviderProps {
  authService: AuthService;
  location?: Location;
  onSignIn?: (userData: User | null) => Promise<void> | void;
  handleApiError?: (error: any) => void;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  authService,
  handleApiError,
  location = window.location
}) => {
  Log.debug('Auth Provider');

  const { authState, loadUser, onError, onLoading, onLogout, handleSignIn } = useAuthenticationContextState();

  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;

    const fn = async () => {
      try {
        onLoading();
        if (hasCodeInUrl(location)) {
          await authService.signInRedirectCallback();
          handleSignIn();
        }

        const user = await authService.tryGetUser();
        if (isMounted.current) {
          loadUser(user);
        }
      } catch (error) {
        onError(error);
      }
    };

    fn();

    return () => {
      isMounted.current = false;
    };
  }, [onLoading, loadUser, onError, authService, location]);

  const signInSilent = useCallback(() => {
    Log.debug('UseCallback : signInSilent');
    onLoading();
    return authService.signInSilent().then((user) => {
      loadUser(user);
      return user;
    });
  }, [authService, loadUser, onLoading]);

  const signInRedirect = useCallback(
    (path?: string) => {
      Log.debug('UseCallback : signInRedirect');
      onLoading();
      authService.signInRedirect(path);
    },
    [authService, onLoading]
  );

  const signOut = useCallback(() => {
    Log.debug('UseCallback : signOut');
    onLoading();
    authService.signOut();
  }, [authService, onLoading]);

  const signOutRedirectCallback = useCallback(() => {
    Log.debug('UseCallback : signOutRedirectCallback');
    authService.signOutRedirectCallback();
    onLogout();
  }, [authService, onLogout]);

  let refresh = false;
  let failedQueue: any[] = [];

  const processQueue = (token: string | null) => {
    failedQueue.forEach((prom) => {
      prom.resolve(token);
    });

    failedQueue = [];
  };

  const createAxiosResponseInterceptor = useCallback(() => {
    if (!authState.user) {
      return;
    }

    const requestInterceptor = axios.interceptors.request.use(async (req) => {
      const payload = parseJwt(authState.user?.access_token as string);
      const expTime = new Date(payload.exp * 1000);
      const nowTime = new Date();
      const compareTime = expTime.getTime() - nowTime.getTime() || 0;

      if (compareTime > 0) {
        return req;
      }
      if (refresh) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            req.headers['Authorization'] = 'Bearer ' + token;
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
            return req;
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }
      refresh = true;
      const user = await authService.signInSilent();
      processQueue(user.access_token);
      req.headers['Authorization'] = `Bearer ${user.access_token}`;
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.access_token;
      refresh = false;
      loadUser(user);
      axios.interceptors.request.eject(requestInterceptor);
      return req;
    });

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response.status !== 401) {
          Log.warn('Error', error.response);
          handleApiError?.(error);
          return Promise.reject(error);
        }

        axios.interceptors.response.eject(interceptor);
        return signInSilent()
          .then((user) => {
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.access_token;
            var axiosConfig = error.response.config;
            axiosConfig.headers.Authorization = 'Bearer ' + user.access_token;
            return axios(axiosConfig);
          })
          .catch((error) => {
            Log.debug('Axios: Sign in Redirect');
            signInRedirect();
            return Promise.reject(error);
          })
          .finally(createAxiosResponseInterceptor);
      }
    );
  }, [authState.user, signInRedirect, signInSilent]);

  createAxiosResponseInterceptor();

  return (
    <AuthContext.Provider
      value={{
        signInSilent,
        signInRedirect,
        signOut,
        signOutRedirectCallback,
        hasPermission: authService.hasPermission,
        ...authState
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const hasCodeInUrl = (location: Location): boolean => {
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.replace('#', '?'));

  return Boolean(
    searchParams.get('code') ||
      searchParams.get('id_token') ||
      searchParams.get('session_state') ||
      hashParams.get('code') ||
      hashParams.get('id_token') ||
      hashParams.get('session_state')
  );
};
